import { useContext, useState, forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip, Typography } from '@mui/material';
import { useFeatureFlagVariantKey } from 'posthog-js/react';
import { AppPaper, FlexCenVer, FlexCenVerSpaceBetween, FlexCol } from '@/UI/styles';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { CreditsContext } from '@/services/CreditsContext';
import { formatNumberToShortString } from '@/utils/numbers';
import { ShareIcon } from '@/UI/Icons/ShareIcon';
import { I18N_KEYS } from '@/language/keys';
import { PermissionsContainer } from '@/components/PermissionsContainer/PermissionsContainer';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { SubscriptionType } from '@/types/shared';
import { FF_DISABLE_SHARE_FOR_FREE_USERS, FF_DISABLE_SHARE_FOR_FREE_USERS_VARIANT_TEST } from '@/consts/featureFlags';
import { color } from '@/colors';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { Tag } from '@/UI/Tag/Tag';
import ShareWorkflowModal from '../ShareWorkflowModal';
import { BatchStatusButton } from '../../RunFlow/TaskManager/BatchStatusButton';
import { CREDITS_INFO_WIDTH } from '../../consts/ui';

const MIN_CREDITS_FOR_LOW_CREDITS = 100;

interface CreditsInfoProps {
  isPaperBg?: boolean;
}

const CreditsInfoComponent = forwardRef<HTMLDivElement, CreditsInfoProps>(({ isPaperBg = true }, ref) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { credits, handleGetMoreCreditsClick } = useContext(CreditsContext);
  const { isAllowedForPermission, isSubscriptionTypeIn } = useSubscriptionPermissions();
  const disableShareExperimentVariant = useFeatureFlagVariantKey(FF_DISABLE_SHARE_FOR_FREE_USERS);
  const isSharingDisabled =
    disableShareExperimentVariant === FF_DISABLE_SHARE_FOR_FREE_USERS_VARIANT_TEST &&
    isSubscriptionTypeIn([SubscriptionType.Free]);

  const { t } = useTranslation();
  const { track } = useAnalytics();

  const handleShare = () => {
    track('shared_workflow_modal_open', {}, TrackTypeEnum.BI);
    setIsShareModalOpen(true);
  };

  const creditsDisplay = useMemo(() => {
    return credits !== null ? formatNumberToShortString(credits) : 0;
  }, [credits]);

  const isLowCredits = useMemo(() => {
    return credits !== null && credits < MIN_CREDITS_FOR_LOW_CREDITS;
  }, [credits]);

  const Container = isPaperBg ? AppPaper : FlexCol;

  return (
    <>
      <Container
        ref={ref}
        sx={{
          ...(isPaperBg
            ? { p: 1, display: 'flex', flexDirection: 'column', width: CREDITS_INFO_WIDTH }
            : { width: '100%' }),
          gap: 1,
        }}
      >
        <FlexCenVerSpaceBetween sx={{ gap: 1 }}>
          <PermissionsContainer permission="credits">
            <Tooltip
              enterDelay={800}
              enterNextDelay={800}
              title={t(I18N_KEYS.FLOW_NAVBAR.GET_MORE_CREDITS_V2)}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                },
              }}
            >
              <FlexCenVer
                sx={{
                  height: 24,
                  gap: 0.25,
                  cursor: 'pointer',
                  pointerEvents: isAllowedForPermission('credits', 'action') ? 'auto' : 'none',
                  px: 0.5,
                  borderRadius: 1,
                  '&:hover': { bgcolor: color.Black84 },
                }}
                onClick={handleGetMoreCreditsClick}
              >
                <AsteriskIcon width={16} height={16} />
                {isLowCredits ? (
                  <FlexCenVer sx={{ gap: 0.75 }}>
                    <Typography variant="body-sm-rg">{creditsDisplay}</Typography>
                    <Tag
                      disableTextTransform
                      text={t(I18N_KEYS.FLOW_NAVBAR.LOW_CREDITS_LABEL)}
                      bgColor={color.Yellow16_T}
                      textColor={color.Yellow100}
                    />
                  </FlexCenVer>
                ) : (
                  <Typography variant="body-sm-rg">
                    {t(I18N_KEYS.GENERAL.CREDITS_DISPLAY, {
                      credits: creditsDisplay,
                    })}
                  </Typography>
                )}
              </FlexCenVer>
            </Tooltip>
          </PermissionsContainer>
          <Tooltip title={isSharingDisabled ? t(I18N_KEYS.SHARE_WORKFLOW_MODAL.FREE_PLAN_TOOLTIP) : ''} placement="top">
            <span>
              <ButtonContained
                onClick={handleShare}
                startIcon={<ShareIcon width={16} height={16} />}
                size="xs"
                mode="filled-light-secondary"
                disabled={isSharingDisabled}
              >
                {t(I18N_KEYS.RECIPE_MAIN.FLOW.SHARE)}
              </ButtonContained>
            </span>
          </Tooltip>
        </FlexCenVerSpaceBetween>
        <Box>
          <BatchStatusButton />
        </Box>
      </Container>
      {isShareModalOpen && <ShareWorkflowModal open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} />}
    </>
  );
});

CreditsInfoComponent.displayName = 'CreditsInfo';

export const CreditsInfo = CreditsInfoComponent;
